import { Transport, senderKey, verboseKey, logKey } from 'data-transport';
export * from 'data-transport';
import { global, identifier, identifierAttribute } from '@ringcentral/mfe-shared';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable no-param-reassign */
const hasParentMFE = (scope) => !!global.name &&
    new RegExp(`^${identifier}`).test(global.name) &&
    scope.test(global.name);
const postMessageToFrames = ({ message, origin: targetOrigin, source, scope, }) => {
    Array.from(document.getElementsByTagName('iframe')).forEach((frame) => {
        const isMFE = !!frame.getAttribute(identifierAttribute);
        const isSameFrame = frame.contentWindow === source;
        // The message transported by the current frame is not being transported to itself.
        if (isMFE && !isSameFrame && scope.test(frame.getAttribute('name'))) {
            frame.contentWindow.postMessage(message, targetOrigin);
        }
    });
};
const broadcastMessage = ({ data: message, origin: targetOrigin, source, scope, }) => {
    if (!(message === null || message === void 0 ? void 0 : message.__DATA_TRANSPORT_UUID__))
        return;
    if (!message.downward) {
        if (hasParentMFE(scope)) {
            global.parent.postMessage(message, targetOrigin);
        }
        else {
            // The mfe apps of the top level, and start spreading the message downward
            message.downward = true;
            postMessageToFrames({
                message,
                origin: targetOrigin,
                source,
                scope,
            });
        }
    }
    else {
        postMessageToFrames({
            message,
            origin: targetOrigin,
            source,
            scope,
        });
    }
};

var _a;
const DEFAULT_TARGET_ORIGIN = '*';
const DEFAULT_TARGET_SCOPE = '*';
const DEFAULT_PREFIX = 'GlobalDataTransport';
const createScopeName = (id, scope = DEFAULT_TARGET_SCOPE) => `${id}:${scope}`;
const getIframeScope = (scope) => new RegExp(`:\\${scope}$`);
class GlobalTransport extends Transport {
    constructor(_a) {
        var { scope = DEFAULT_TARGET_SCOPE, targetOrigin = DEFAULT_TARGET_ORIGIN, prefix = DEFAULT_PREFIX, listener = function listener(callback) {
            const handler = ({ data, source, }) => {
                if (!data || (!data.action && !this.isGlobalTransport(data.action)))
                    return;
                if (data.scope === this.targetScope) {
                    callback(data);
                }
                const scope = getIframeScope(this.targetScope);
                broadcastMessage({
                    data,
                    origin: this.targetOrigin,
                    source,
                    scope,
                });
            };
            global.addEventListener('message', handler);
            return () => {
                global.removeEventListener('message', handler);
            };
        }, sender = function sender(message) {
            message.scope = this.targetScope;
            const scope = getIframeScope(this.targetScope);
            if (hasParentMFE(scope)) {
                global.parent.postMessage(message, this.targetOrigin);
            }
            else {
                global.postMessage(message, this.targetOrigin);
            }
        }, checkListen = false, serializer = {
            stringify: JSON.stringify,
            parse: JSON.parse,
        } } = _a, options = __rest(_a, ["scope", "targetOrigin", "prefix", "listener", "sender", "checkListen", "serializer"]);
        super(Object.assign(Object.assign({}, options), { prefix,
            checkListen,
            listener,
            sender,
            serializer }));
        this.eventListeners = new Map();
        const regExp = new RegExp(`^${prefix}`);
        this.isGlobalTransport = (str) => regExp.test(str);
        this.targetOrigin = targetOrigin;
        this.targetScope = scope;
        const _sender = this[senderKey];
        this[senderKey] = (data) => {
            if (data.type === 'response') {
                // reset the downward flag with response message
                delete data.downward;
            }
            _sender(data);
        };
        const { dispose } = this;
        this.dispose = () => {
            dispose();
            this.eventListeners = new Map();
        };
    }
    /**
     * Set the target origin
     */
    setOrigin(targetOrigin) {
        this.targetOrigin = targetOrigin;
    }
    /**
     * Set verbose mode
     */
    setVerbose(value) {
        this[verboseKey] = value;
    }
    /**
     * Set a custom logger
     */
    setLogger(logger) {
        this[logKey] = logger;
    }
    /**
     * Set the target scope
     */
    setScope(scope) {
        this.targetScope = scope;
    }
    /**
     * Listen an event that transport data.
     *
     * @param name A transport action as listen message data action type
     * @param fn A transport listener
     */
    listen(name, fn) {
        var _a;
        const eventListeners = (_a = this.eventListeners.get(name)) !== null && _a !== void 0 ? _a : new Set();
        if (eventListeners.size === 0) {
            const callback = super.listen(name, ((...args) => {
                return Promise.race(Array.from(eventListeners)
                    .slice(1)
                    .map((fn) => fn(...args)));
            }));
            eventListeners.add(callback);
        }
        eventListeners.add(fn);
        this.eventListeners.set(name, eventListeners);
        return () => {
            eventListeners.delete(fn);
            if (eventListeners.size === 1) {
                eventListeners.values().next().value();
                eventListeners.clear();
            }
        };
    }
}
global.__RC_TRANSPORT__ = (_a = global.__RC_TRANSPORT__) !== null && _a !== void 0 ? _a : new GlobalTransport({});
const globalTransport = global.__RC_TRANSPORT__;
const getGlobalTransport = () => globalTransport;

export { GlobalTransport, createScopeName, getGlobalTransport, globalTransport };
//# sourceMappingURL=index.mjs.map
